import { useState, useCallback, useEffect } from 'react';
import { generateId, getStoredData, STORAGE_KEYS, createDebouncedCloudStorage, logger } from '@/utils';
import { mondayService } from '@/services/api';
import type { Lead, LeadList, ListType } from '@/types';

const debouncedLeadListsStorage = createDebouncedCloudStorage<LeadList[]>(STORAGE_KEYS.LEAD_LISTS, 'lead-lists', 3000);

interface UseListsDeps {
  leads: Lead[];
  setLeads: React.Dispatch<React.SetStateAction<{ data: Lead[]; loading: boolean; error: string | null }>>;
  enrichLeadsParallel: (leadsToEnrich: Lead[], onProgress?: (completed: number, total: number) => void) => Promise<Map<string, { enriched: boolean; data?: Lead['apolloData'] }>>;
  useMockData: boolean;
}

export function useLists({ leads, setLeads, enrichLeadsParallel, useMockData }: UseListsDeps) {
  const [leadLists, setLeadLists] = useState<LeadList[]>(() => {
    const stored = getStoredData<LeadList[]>(STORAGE_KEYS.LEAD_LISTS, []);
    return stored.map((list) => ({
      ...list,
      listType: list.listType || 'company',
    }));
  });

  const [selectedList, setSelectedList] = useState<LeadList | null>(null);
  const [enrichingLists, setEnrichingLists] = useState<Set<string>>(new Set());
  const [syncingLists, setSyncingLists] = useState<Set<string>>(new Set());

  // Persist lead lists to localStorage + DynamoDB
  useEffect(() => {
    debouncedLeadListsStorage.write(leadLists);
  }, [leadLists]);

  // Load from DynamoDB on mount (if localStorage is empty)
  useEffect(() => {
    if (leadLists.length === 0) {
      debouncedLeadListsStorage.loadFromCloud().then(cloudData => {
        if (cloudData && cloudData.length > 0) {
          setLeadLists(cloudData.map(list => ({ ...list, listType: list.listType || 'company' })));
        }
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createList = useCallback((name: string, description: string, listType: ListType = 'company') => {
    const today = new Date().toISOString().split('T')[0] ?? '';
    const newList: LeadList = {
      id: generateId('list'),
      name,
      description,
      listType,
      createdAt: today,
      leads: [],
      visitors: [],
      campaign: null,
    };
    setLeadLists((prev) => [...prev, newList]);
    return newList.id;
  }, []);

  const deleteList = useCallback((listId: string) => {
    setLeadLists((prev) => prev.filter((list) => list.id !== listId));
    setSelectedList(null);
  }, []);

  const renameList = useCallback((listId: string, name: string, description?: string) => {
    setLeadLists((prev) =>
      prev.map((list) =>
        list.id === listId ? { ...list, name, ...(description !== undefined ? { description } : {}) } : list
      )
    );
  }, []);

  const markListEnriched = useCallback((listId: string) => {
    const list = leadLists.find((l) => l.id === listId);
    if (!list) return;

    setLeads((prev) => ({
      ...prev,
      data: prev.data.map((lead) =>
        list.leads.includes(lead.id)
          ? { ...lead, apolloEnriched: true }
          : lead
      ),
    }));
  }, [leadLists, setLeads]);

  const addToList = useCallback(
    (listId: string, type: 'leads' | 'visitors', ids: (string | number)[]) => {
      setLeadLists((prev) =>
        prev.map((list) => {
          if (list.id === listId) {
            if (type === 'leads') {
              return {
                ...list,
                leads: [...new Set([...list.leads, ...(ids as string[])])],
              };
            } else {
              return {
                ...list,
                visitors: [...new Set([...list.visitors, ...(ids as number[])])],
              };
            }
          }
          return list;
        })
      );
    },
    []
  );

  const removeFromList = useCallback(
    (listId: string, type: 'leads' | 'visitors', ids: (string | number)[]) => {
      setLeadLists((prev) =>
        prev.map((list) => {
          if (list.id === listId) {
            if (type === 'leads') {
              return {
                ...list,
                leads: list.leads.filter((id) => !ids.includes(id)),
              };
            } else {
              return {
                ...list,
                visitors: list.visitors.filter((id) => !ids.includes(id as number)),
              };
            }
          }
          return list;
        })
      );
    },
    []
  );

  const enrichList = useCallback(
    async (listId: string) => {
      const list = leadLists.find((l) => l.id === listId);
      if (!list) return { enriched: 0, skipped: 0 };

      setEnrichingLists((prev) => new Set(prev).add(listId));

      try {
        const leadsToEnrich = leads.filter(
          (lead) => list.leads.includes(lead.id) && !lead.apolloEnriched
        );

        if (leadsToEnrich.length === 0) {
          return { enriched: 0, skipped: 0 };
        }

        const results = await enrichLeadsParallel(leadsToEnrich);

        let enriched = 0;
        let skipped = 0;

        setLeads((prev) => ({
          ...prev,
          data: prev.data.map((lead) => {
            const result = results.get(lead.id);
            if (result) {
              if (result.enriched && result.data) {
                enriched++;
                return { ...lead, apolloEnriched: true, apolloData: result.data };
              } else {
                skipped++;
              }
            }
            return lead;
          }),
        }));

        return { enriched, skipped };
      } finally {
        setEnrichingLists((prev) => {
          const next = new Set(prev);
          next.delete(listId);
          return next;
        });
      }
    },
    [leadLists, leads, enrichLeadsParallel, setLeads]
  );

  const syncListToMonday = useCallback(
    async (listId: string) => {
      const list = leadLists.find((l) => l.id === listId);
      if (!list) return { synced: 0, alreadySynced: 0 };

      setSyncingLists((prev) => new Set(prev).add(listId));

      try {
        const leadsToSync = leads.filter(
          (lead) => list.leads.includes(lead.id) && lead.apolloEnriched && !lead.syncedToMonday
        );

        const alreadySynced = leads.filter(
          (lead) => list.leads.includes(lead.id) && lead.apolloEnriched && lead.syncedToMonday
        ).length;

        if (leadsToSync.length === 0) {
          logger.log(`[useLists] No new leads to sync from list "${list.name}" (${alreadySynced} already synced)`);
          return { synced: 0, alreadySynced };
        }

        const today = new Date().toISOString();

        if (useMockData) {
          await new Promise((resolve) => setTimeout(resolve, 1000));

          setLeads((prev) => ({
            ...prev,
            data: prev.data.map((lead) =>
              leadsToSync.some((l) => l.id === lead.id)
                ? { ...lead, syncedToMonday: true, syncedToMondayAt: today }
                : lead
            ),
          }));

          logger.log(`[useLists] Synced ${leadsToSync.length} leads from list "${list.name}" to Monday`);
          return { synced: leadsToSync.length, alreadySynced };
        } else {
          const syncedIds: string[] = [];
          const failedIds: string[] = [];

          for (const lead of leadsToSync) {
            try {
              await mondayService.updateLead(lead.id, {
                apolloEnriched: true,
                apolloData: lead.apolloData,
              });
              syncedIds.push(lead.id);

              setLeads((prev) => ({
                ...prev,
                data: prev.data.map((l) =>
                  l.id === lead.id
                    ? { ...l, syncedToMonday: true, syncedToMondayAt: today }
                    : l
                ),
              }));
            } catch (err) {
              logger.simpleError(`[useLists] Failed to sync lead ${lead.id}:`, err);
              failedIds.push(lead.id);
            }
          }

          if (failedIds.length > 0) {
            logger.warn(`[useLists] Sync partially failed: ${syncedIds.length} synced, ${failedIds.length} failed`);
          }

          return { synced: syncedIds.length, alreadySynced, failed: failedIds.length };
        }
      } finally {
        setSyncingLists((prev) => {
          const next = new Set(prev);
          next.delete(listId);
          return next;
        });
      }
    },
    [leadLists, leads, useMockData, setLeads]
  );

  const assignCampaignToList = useCallback((listId: string, campaignId: string | null) => {
    setLeadLists((prev) =>
      prev.map((list) =>
        list.id === listId ? { ...list, campaign: campaignId } : list
      )
    );
  }, []);

  const getListsForLead = useCallback(
    (leadId: string) => {
      return leadLists.filter((list) => list.leads.includes(leadId));
    },
    [leadLists]
  );

  return {
    leadLists,
    selectedList,
    setSelectedList,
    enrichingLists,
    syncingLists,

    // Actions
    createList,
    deleteList,
    renameList,
    markListEnriched,
    addToList,
    removeFromList,
    enrichList,
    syncListToMonday,
    assignCampaignToList,
    getListsForLead,

    // Internal (for orchestrator / cross-domain hooks)
    setLeadLists,
  };
}

export type UseListsReturn = ReturnType<typeof useLists>;
